import type { ComponentType } from 'react';
import OcrMotion from './OcrMotion';
import OcrMotionA from './ocr/OcrMotionA';
import OcrMotionB from './ocr/OcrMotionB';
import OcrMotionC from './ocr/OcrMotionC';

type Variant = {
  id: string;
  name: string;
  tagline: string;
  description: string;
  Component: ComponentType<{ cycleSeconds?: number }>;
};

const VARIANTS: Variant[] = [
  {
    id: 'current',
    name: '00 · Current (production)',
    tagline: 'Linear scan-beam, abstract raster bars.',
    description:
      'What is live today. Reads like a flatbed scanner: horizontal beam descends, grey raster bars stand in for text, bounding boxes settle on each word.',
    Component: OcrMotion,
  },
  {
    id: 'a',
    name: 'A · Texture pass',
    tagline: 'Same metaphor, made physical.',
    description:
      'Keeps the scan-beam but replaces raster bars with real readable Arztbrief text, adds a 1.2° page skew + drop shadow, shows one OCR mis-read being corrected mid-cycle, and prints per-token confidence pills in the transcript. Cheapest of the three.',
    Component: OcrMotionA,
  },
  {
    id: 'b',
    name: 'B · Region detection → JSON',
    tagline: 'Looks like a modern layout model.',
    description:
      'Two phases: layout-detection flashes all semantic regions at once (HEADER, PATIENT, DIAGNOSE, BEFUND, LABOR, MEDIKATION, …), then a structured JSON extract streams to the right with the currently-extracting region pulsing on the source. No scan beam. Directly proves the "Tabellen als HTML, Formeln als LaTeX, JSON · Markdown" claim from the spec card.',
    Component: OcrMotionB,
  },
  {
    id: 'c',
    name: 'C · Token-streaming VLM',
    tagline: 'Reads like a VLM thinking out loud.',
    description:
      'Whole document visible from frame 1. Output streams in semantic — not reading — order, with an attention rectangle hopping between regions as each token is emitted. Includes one self-correction beat (model types "7.2", hesitates, deletes, writes "7.4"). Most "AI-feeling" of the three; also the most aggressive claim.',
    Component: OcrMotionC,
  },
];

export default function Prototypes() {
  return (
    <div className="min-h-screen bg-bg text-fg">
      <header className="border-b border-rule">
        <div className="mx-auto flex max-w-[1280px] items-baseline justify-between gap-6 px-6 py-6 md:px-10">
          <div>
            <p className="label-mono text-accent">— internal · ocr motion prototypes</p>
            <h1 className="mt-2 text-2xl font-bold uppercase tracking-[-0.01em] text-ink md:text-3xl">
              Welche OCR-Animation fühlt sich am echtesten an?
            </h1>
          </div>
          <a
            href="#"
            className="border border-rule px-4 py-2 font-mono text-[11px] uppercase tracking-[0.12em] text-fg-muted hover:border-fg-muted hover:text-fg"
          >
            ← zurück zur Landing
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-[1280px] space-y-16 px-6 py-12 md:px-10 md:py-16">
        {VARIANTS.map((v) => (
          <section key={v.id} id={v.id} className="space-y-5">
            <div className="grid gap-6 md:grid-cols-12">
              <div className="md:col-span-4">
                <h2 className="font-mono text-xs uppercase tracking-[0.14em] text-accent">
                  {v.name}
                </h2>
                <p className="mt-2 text-lg font-medium text-ink">{v.tagline}</p>
              </div>
              <p className="text-sm leading-relaxed text-fg-muted md:col-span-8">{v.description}</p>
            </div>
            <div className="border border-rule bg-bg-elev">
              <div className="flex items-baseline justify-between border-b border-rule px-5 py-3 font-mono text-[11px] uppercase tracking-[0.12em] text-fg-muted">
                <span>
                  <span className="font-semibold text-accent">demo</span> · ocr.{v.id}
                </span>
                <span className="hidden sm:inline">Beispiel: Arztbrief</span>
                <span>{v.tagline}</span>
              </div>
              <v.Component />
            </div>
          </section>
        ))}

        <footer className="border-t border-rule pt-8 text-center font-mono text-[11px] uppercase tracking-[0.12em] text-fg-muted">
          Internal preview · not linked from production
        </footer>
      </main>
    </div>
  );
}
